import { onMount, onCleanup, createEffect, Accessor } from 'solid-js';
import { computePosition, shift, flip } from '@floating-ui/dom';
import { isPointerEventInsideRoot } from '@trimble/solidjs-utils';

export interface UseLayerPopupParams {
  popupRef: Accessor<HTMLDivElement | undefined>;
  anchorElement: Accessor<HTMLElement>;
  onClose: Accessor<() => void>;
  /**
   * Invoked **synchronously** in the document `mousedown` handler when the click is
   * outside the popup and anchor, **before** the deferred `onClose` runs. Use to persist
   * in-progress state (e.g. read the name field) — `queueMicrotask` / a timer close is too
   * early for the browser to have fired `blur` / `inputBlur` on the still-focused control.
   */
  onBeforeOutsideClose?: Accessor<() => void>;
}

/**
 * Shared hook for layer popup positioning and click-outside handling.
 * Sets up lifecycle hooks for positioning and closing the popup.
 */
export const useLayerPopup = (params: UseLayerPopupParams): void => {
  const { popupRef, anchorElement, onClose, onBeforeOutsideClose } = params;
  const POPUP_OFFSET_X = 205;
  const POPUP_OFFSET_Y = 12;
  let pendingCloseTimer: ReturnType<typeof setTimeout> | null = null;

  // Position the popup relative to the anchor element
  const updatePosition = (): void => {
    const popup = popupRef();
    const anchor = anchorElement();
    if (popup && anchor) {
      void computePosition(anchor, popup, {
        placement: 'bottom-start',
        middleware: [shift(), flip()],
      }).then(({ x, y }) => {
        const currentPopup = popupRef();
        if (currentPopup) {
          Object.assign(currentPopup.style, {
            left: `${x - POPUP_OFFSET_X}px`,
            top: `${y - POPUP_OFFSET_Y}px`,
          });
        }
      });
    }
  };

  const handleClickOutside = (event: MouseEvent): void => {
    const popup = popupRef();
    const anchor = anchorElement();
    const close = onClose();

    if (
      !popup ||
      !close ||
      isPointerEventInsideRoot(event, popup) ||
      (anchor != null && isPointerEventInsideRoot(event, anchor))
    ) {
      return;
    }
    // Synchronously run `onBeforeOutsideClose` while the pre-click focused element
    // (e.g. text input) is still attached — mousedown on document fires before the UA
    // retargets focus, so a microtask/RAF can unmount the popup *before* `inputBlur` fires.
    const before = onBeforeOutsideClose?.();
    before?.();

    // `setTimeout(0)` runs in a new task, after the browser's default mousedown
    // processing (incl. focusout/blur) when applicable — unlike `queueMicrotask`, which
    // can run before that.
    if (pendingCloseTimer != null) {
      clearTimeout(pendingCloseTimer);
    }
    pendingCloseTimer = setTimeout(() => {
      pendingCloseTimer = null;
      close();
    }, 0);
  };

  // Reactively watch for popup ref to become available and position it
  createEffect(() => {
    const popup = popupRef();
    if (popup) {
      // Use queueMicrotask to ensure DOM is fully rendered
      queueMicrotask(() => {
        updatePosition();
      });
    }
  });

  onMount(() => {
    // Update position on window resize
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    document.addEventListener('mousedown', handleClickOutside);
  });

  onCleanup(() => {
    if (pendingCloseTimer != null) {
      clearTimeout(pendingCloseTimer);
      pendingCloseTimer = null;
    }
    window.removeEventListener('resize', updatePosition);
    window.removeEventListener('scroll', updatePosition, true);
    document.removeEventListener('mousedown', handleClickOutside);
  });
};
